interface CalendarDateParams {
    year: number;
    month: number;
    day: number;
}
class CalendarDate {
    month: number;
    day: number;
    year: number;

    constructor({ year, month, day }: CalendarDateParams) {
        this.month = month;
        this.day = day;
        this.year = year;
    }

    toJSON(): CalendarDateParams {
        return {
            year: this.year,
            month: this.month,
            day: this.day,
        };
    }

    toString(): string {
        return `${this.year}-${this.month.toString().padStart(2, '0')}-${this.day.toString().padStart(2, '0')}`;
    }

    static fromDate(date: Date): CalendarDate {
        return new CalendarDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
    }

    static parse(str: String): CalendarDate | undefined {
        try {
            const [year, month, day] = str.split('-').map((x) => parseInt(x, 10));
            return new CalendarDate({ year, month, day });
        } catch (e) {
            return undefined;
        }
    }
}

export interface TripleParams {
    id: string;
    triplet: string;
    entries: Array<TripleEntryParams>;
    offset?: number;
    date?: CalendarDateParams;
}

class Triple {
    readonly id: string;
    readonly triplet: string;
    readonly entries: Array<TripleEntry>;
    readonly offset: number;
    readonly date?: CalendarDate;

    constructor(params: TripleParams) {
        if (params.id == null || params.triplet == null || params.entries == null || params.entries.length !== 3) {
            throw new Error('Invalid Triple params: ' + JSON.stringify(params));
        }
        Object.assign(this, params);

        if (this.date && !(this.date instanceof CalendarDate)) {
            this.date = new CalendarDate(this.date);
        }

        this.entries = this.entries.map((entry) => (entry instanceof TripleEntry ? entry : new TripleEntry(entry)));
    }

    toJSON(): TripleParams {
        return {
            id: this.id,
            triplet: this.triplet,
            offset: this.offset,
            entries: this.entries.map((entry) => entry.toJSON()),
            date: this.date?.toJSON(),
        };
    }

    answers() {
        return this.entries.map((entry) => entry.answer);
    }

    getOrderedEntries() {
        return [...this.entries].sort((a, b) => a.answer.length - b.answer.length);
    }

    copyWithOrderedEntries(): Triple {
        const params: TripleParams = {
            ...this.toJSON(),
            entries: this.getOrderedEntries().map((e) => e.toJSON()),
        };
        return new Triple(params);
    }

    getEntryByLength(length: number) {
        if (length < 4 || length > 6) {
            throw new Error(`Invalid entry length: ${length}`);
        }
        const res = this.entries.find((entry) => entry.answer.length === length);
        if (!res) {
            throw new Error(`No entry found with length ${length} in triplet ${this.triplet}`);
        }
        return res;
    }

    copyAndClearDate(): Triple {
        return new Triple(Object.assign({}, this, { date: undefined }));
    }

    toTsvRow() {
        return [
            this.id,
            this.entries[0].answer,
            this.entries[1].answer,
            this.entries[2].answer,
            this.triplet,
            this.entries[0].clue,
            this.entries[1].clue,
            this.entries[2].clue,
        ].join('\t');
    }
}

interface TripleEntryParams {
    clue: string;
    answer: string;
}

class TripleEntry implements TripleEntryParams {
    clue: string;
    answer: string;

    constructor(params: TripleEntryParams) {
        this.clue = params.clue;
        this.answer = params.answer;
    }

    toJSON(): TripleEntryParams {
        return {
            clue: this.clue,
            answer: this.answer,
        };
    }
}

export { Triple, TripleEntry, CalendarDate };
